/**
 * Shared polling loop for Celery ``report_v2_regeneration`` jobs.
 * Keeps UX consistent between Report Content tab, QA tab, and review flows.
 */

import React from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { Toast } from '../stores/toast';

export interface UseV2RegenerationJobPollerParams {
  reportId: string;
  queryClient: QueryClient;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  /** Latest report snapshot (optional; improves version picker after completion). */
  report?: unknown;
  selectedVersion?: string | null;
  onSelectedVersionChange?: (version: string) => void;
}

/**
 * Polls ``getReportJobStatus`` until completion/failure or timeout,
 * emitting toasts and invalidating ``['report', reportId]`` on success.
 */
export function useV2RegenerationJobPoller({
  reportId,
  queryClient,
  addToast,
  report,
  selectedVersion,
  onSelectedVersionChange,
}: UseV2RegenerationJobPollerParams) {
  return React.useCallback(
    async (
      newJobId: string,
      kickoffMessage = 'V2 Regeneration Queued',
      options?: {
        onProgress?: (p: {
          percentage: number | null;
          stage: string | null;
        }) => void;
      },
    ): Promise<void> => {
      const { getReportJobStatus } = await import('../api/reports');
      addToast({
        type: 'info',
        title: kickoffMessage,
        message: `Job ${newJobId.substring(0, 8)}… is running.`,
      });

      let completed = false;
      let checkCount = 0;
      const maxChecks = 240;
      try {
        while (!completed && checkCount < maxChecks) {
          await new Promise((resolve) => setTimeout(resolve, 2500));
          checkCount++;
          try {
            const jobStatus = await getReportJobStatus(newJobId);
            const pct = (jobStatus as { progress_data?: { percentage?: unknown } })?.progress_data
              ?.percentage;
            const stage =
              (jobStatus as { progress_data?: { stage_message?: string; current_stage?: string } })?.progress_data
                ?.stage_message ||
              (jobStatus as { progress_data?: { current_stage?: string } })?.progress_data?.current_stage;
            if (typeof pct === 'number' || typeof stage === 'string') {
              options?.onProgress?.({
                percentage: typeof pct === 'number' ? pct : null,
                stage: typeof stage === 'string' ? stage : null,
              });
            }

            if (jobStatus.status === 'completed') {
              completed = true;
              addToast({
                type: 'success',
                title: 'V2 Regeneration Complete',
                message: 'Executive summary has been regenerated successfully',
              });
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
              queryClient.invalidateQueries({ queryKey: ['report-v2-content', reportId] });
              queryClient.invalidateQueries({ queryKey: ['report-v1-content', reportId] });
              queryClient.invalidateQueries({ queryKey: ['report-staff-assignments', reportId] });
              setTimeout(() => {
                queryClient.refetchQueries({ queryKey: ['report', reportId] }).then(() => {
                  if (typeof onSelectedVersionChange === 'function') {
                    const meta = (
                      typeof report === 'object' &&
                      report !== null &&
                      'meta' in report &&
                      (report as { meta?: { current_version?: number } }).meta?.current_version != null
                    )
                      ? (report as { meta: { current_version: number } }).meta.current_version
                      : undefined;
                    if (meta != null) {
                      onSelectedVersionChange(`v${meta}`);
                    } else if (selectedVersion?.startsWith?.('v')) {
                      const n = parseInt(selectedVersion.slice(1), 10);
                      if (!Number.isNaN(n)) onSelectedVersionChange(`v${n + 1}`);
                    } else {
                      onSelectedVersionChange?.('v2');
                    }
                  }
                });
              }, 500);
            } else if (jobStatus.status === 'failed') {
              completed = true;
              addToast({
                type: 'error',
                title: 'V2 Regeneration Failed',
                message: jobStatus.error_message || 'Unknown error',
              });
            }
          } catch {
            /* keep polling */
          }
        }

        if (!completed) {
          addToast({
            type: 'info',
            title: 'V2 Regeneration Still Running',
            message:
              'Regeneration is taking longer than expected. It will continue in the background; refresh or open Report Content to pick up progress.',
          });
        }
      } finally {
        options?.onProgress?.({ percentage: null, stage: null });
      }
    },
    [
      addToast,
      queryClient,
      reportId,
      report,
      selectedVersion,
      onSelectedVersionChange,
    ],
  );
}
